import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ProviderConsumer, Text } from '@fluentui/react-northstar';
import { PersonType, UserType } from '@microsoft/mgt-components';
import { PeoplePicker } from '@microsoft/mgt-react';
import i18next from 'i18next';
import { platformService } from '../../services/platform.service';
import { searchUsers } from '../../cache/PlatformUserCache';
import { getCachedFeature } from '../../cache/FeatureCache';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { getDropdownBoxShadow } from '../../../tikit/ticketHelper';
import { LICENSE_FEATURE } from '../../utils/constants';
import { CELL_BOX, PANEL_PORTAL, byCodeUnit, markClass, reportFailure, savingCell, useSaveMark, focusCellBack } from './inlineCell';
import { reloadAfterSave } from './InlineFieldCells';
import { PanelActions, focusActions, holdOutsideClick, isPanelAction, pickerListOpen, wrapActionTab } from './PanelActions';
import { usePanelPlacement } from './TypeaheadList';
import { FieldErrorText, RequiredAsterisk } from '../FieldErrorText';

export interface ListUser {
  Id?: number;
  FullName?: string;
  UserName?: string;
  Email?: string;
  AadObjectId?: string;
  GivenName?: string;
  FamilyName?: string;
}

interface AffectedUser {
  PlatformUserId: number;
  PlatformUser?: ListUser;
}

/** mgt types selectionChanged as a plain Event; the people chosen ride on it as a CustomEvent's detail. */
export const pickedFrom = (event: Event): PickedPerson[] => (event as CustomEvent<PickedPerson[]>).detail ?? [];

/** What mgt's people picker hands back for a chosen person. */
export interface PickedPerson {
  id?: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  userPrincipalName?: string;
  scoredEmailAddresses?: { address?: string }[];
}

interface Props {
  ticketId: number;
  requester: ListUser | undefined;
  affectedUsers: AffectedUser[];
  /** The cell as the list draws it today: the requester, or the requester and affected users' avatars. */
  resting: React.ReactElement;
  /** Asked again on the server after a save, so a ticket the view no longer matches leaves it. */
  onSaved: () => void | Promise<void>;
  /**
   * Puts the saved people into the row at once, so the cell shows them and goes green without waiting for the
   * reload. Without it (My Work) the green waits for the reload.
   */
  onPatch?: (patch: RequesterPatch) => void;
  /** Runs before the save and throws InlineEditRefused to stop it. */
  guard?: () => Promise<void>;
}

interface RequesterPatch { RequesterId?: number; Requester?: ListUser; RequesterName?: string; AffectedUsers?: AffectedUser[] }

const PANEL_WIDTH = 340;

/** The picker's own form of a Tikit user, as the ticket page seeds it. */
export const toPerson = (user: ListUser | undefined): PickedPerson => ({
  displayName: user?.FullName,
  userPrincipalName: user?.UserName,
  id: user?.AadObjectId
});

export const personKey = (person: PickedPerson | undefined) => (person?.id || person?.userPrincipalName || '').toLowerCase();

export const toPlatformUser = (person: PickedPerson) => ({
  AadObjectId: person.id,
  FullName: person.displayName,
  GivenName: person.givenName,
  FamilyName: person.surname,
  Email: person.scoredEmailAddresses?.[0]?.address,
  UserName: person.userPrincipalName
} as PlatformUser);

/**
 * The Requester column, edited in place: a panel under the cell with the ticket page's two pickers, Requester
 * and Affected Users. Saves when the panel is left (a click outside it, or Tab out of it); Escape puts both
 * back. Saved through the ticket page's own calls - RequesterId on the ticket, then UpdateAffectedUsers.
 */
export const InlineRequesterCell = ({ ticketId, requester, affectedUsers, resting, onSaved, onPatch, guard }: Props) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Requester is required: emptied, it says so under the box as the ticket page does, and leaving keeps the ticket's requester. */
  const [requesterEmpty, setRequesterEmpty] = useState(false);
  const [affectedUsersEnabled, setAffectedUsersEnabled] = useState(false);

  const cellRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Follows the cell as the grid scrolls either way, and stays within the view's left and right.
  const position = usePanelPlacement(cellRef, panelRef, open, PANEL_WIDTH, 260);
  const pickedRequesterRef = useRef<PickedPerson | null>(null);
  const pickedAffectedRef = useRef<PickedPerson[] | null>(null);
  const closingRef = useRef(false);
  /** Set to reopen the panel after a failed save with the agent's picks in it, not the ticket's people. */
  const reopeningRef = useRef(false);
  const affectedKey = affectedUsers.map(a => a.PlatformUserId).join(',');
  const saveMark = useSaveMark(ticketId, `${requester?.Id}|${affectedKey}`, saving, 'requester');
  /** Redraws when a pick changes, so Save can light - the picks themselves live in refs (see the seeding below). */
  const [, setPicksChanged] = useState(0);

  useEffect(() => {
    getCachedFeature(LICENSE_FEATURE.AffectedUsers).then(setAffectedUsersEnabled);
  }, []);

  // Rows are keyed by position, so a refetch can hand this cell a different ticket: stop looking busy. New people
  // for this ticket - put in the row by its own save as it starts, or by a reload - only close it.
  useEffect(() => { setSaving(false); }, [ticketId]);
  useEffect(() => { setOpen(false); }, [ticketId, requester?.Id, affectedKey]);


  // The pickers keep what they are given only once: mgt-react writes every prop onto the element on each
  // render, so a selection passed as a prop would be put back over the agent's picks. Seeded once instead,
  // as the ticket page does - when the panel exists, which is a render after it opens (it waits for its
  // position).
  const panelShown = open && position != null;
  useEffect(() => {
    if (!panelShown) return;
    const panel = panelRef.current;
    const requesterPicker = panel?.querySelector<HTMLElement & { selectedPeople: PickedPerson[] }>('.inline-requester-picker');
    const affectedPicker = panel?.querySelector<HTMLElement & { selectedPeople: PickedPerson[] }>('.inline-affected-picker');
    const reopening = reopeningRef.current;
    reopeningRef.current = false;
    let seedRequester: PickedPerson[] = [];
    if (reopening && pickedRequesterRef.current) seedRequester = [pickedRequesterRef.current];
    else if (requester) seedRequester = [toPerson(requester)];
    if (requesterPicker) requesterPicker.selectedPeople = seedRequester;
    setRequesterEmpty(!seedRequester.length);
    if (affectedPicker) affectedPicker.selectedPeople = reopening && pickedAffectedRef.current ? pickedAffectedRef.current : affectedUsers.map(a => toPerson(a.PlatformUser));
    // Seeding makes the pickers draw their insides again, which replaces their inputs - focused before that,
    // the cursor is thrown out to the page. Wait for the redraw (mgt's elements are lit elements). A single
    // picker holding its one person has no input at all, so the cursor goes to the first picker that has
    // one, else to the panel, which still hears Escape.
    const pickers = [requesterPicker, affectedPicker].filter(Boolean) as (HTMLElement & { updateComplete?: Promise<unknown>; input?: HTMLElement })[];
    Promise.all(pickers.map(picker => picker.updateComplete ?? Promise.resolve())).then(() => {
      const withInput = pickers.find(picker => picker.input);
      if (withInput) withInput.focus();
      else panel?.focus();
    });
  }, [panelShown]);

  const changes = () => {
    const pickedRequester = pickedRequesterRef.current;
    const pickedAffected = pickedAffectedRef.current;
    const originalAffected = affectedUsers.map(a => personKey(toPerson(a.PlatformUser))).sort(byCodeUnit).join('|');
    return {
      pickedRequester,
      pickedAffected,
      requesterChanged: pickedRequester != null && personKey(pickedRequester) !== personKey(toPerson(requester)),
      affectedChanged: pickedAffected != null && pickedAffected.map(personKey).sort(byCodeUnit).join('|') !== originalAffected
    };
  };
  const { requesterChanged: requesterDirty, affectedChanged: affectedDirty } = changes();
  const dirty = requesterDirty || affectedDirty;
  const canSave = dirty && !requesterEmpty;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;

  const save = async () => {
    const { pickedRequester, pickedAffected, requesterChanged, affectedChanged } = changes();
    if (!requesterChanged && !affectedChanged) return;

    saveMark.begin();
    setSaving(true);
    let patched = false;
    try {
      // Refused first: finding a person can add them as a Tikit user.
      await guard?.();
      // Then the people (each found or added), so the row can show them while the save is out.
      const newRequester = requesterChanged ? await searchUsers(toPlatformUser(pickedRequester)) : null;
      const newAffected: ListUser[] = [];
      if (affectedChanged) for (const person of pickedAffected) newAffected.push(await searchUsers(toPlatformUser(person)));
      const patch: RequesterPatch = {};
      if (newRequester) Object.assign(patch, { RequesterId: newRequester.Id, Requester: newRequester, RequesterName: newRequester.FullName });
      if (affectedChanged) patch.AffectedUsers = newAffected.map(user => ({ PlatformUserId: user.Id, PlatformUser: user }));
      if (onPatch) { onPatch(patch); patched = true; }

      const api = new platformService();
      if (newRequester) await api.updateTicket(ticketId, { RequesterId: newRequester.Id });
      if (affectedChanged) await api.UpdateAffectedUsers(ticketId, newAffected.map(user => user.Id));
      if (onPatch) reloadAfterSave(onSaved);
      else await onSaved();
      setSaving(false);
      saveMark.show('saved');
    } catch (error: unknown) {
      // Back to the ticket's own people; the reload below then shows whatever did save.
      if (patched) onPatch?.({
        ...(requesterChanged ? { RequesterId: requester?.Id, Requester: requester, RequesterName: requester?.FullName } : {}),
        ...(affectedChanged ? { AffectedUsers: affectedUsers } : {})
      });
      const outcome = reportFailure(error, {});
      setSaving(false);
      saveMark.show(outcome);
      // Fetched again whatever failed, so a requester saved before the affected users failed still shows. If
      // nothing went through, the panel reopens with the agent's picks.
      await Promise.resolve(onSaved()).catch(() => undefined);
      if (outcome === 'failed') {
        reopeningRef.current = true;
        closingRef.current = false;
        setOpen(true);
      }
    }
  };

  const close = (keep: boolean, refocus: boolean) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setOpen(false);
    if (keep) save();
    if (refocus) focusCellBack(cellRef.current);
  };

  // A click outside both the cell and the panel closes it - unless something has changed, which only Save,
  // Cancel or Escape settle. composedPath reaches into the pickers' shadow roots, so picking from their own
  // suggestion lists still counts as inside.
  useEffect(() => {
    if (!open) return;
    closingRef.current = false;
    const onMouseDown = (event: MouseEvent) => {
      const path = event.composedPath();
      if (path.includes(panelRef.current) || path.includes(cellRef.current)) return;
      if (dirtyRef.current) holdOutsideClick(event, panelRef.current, cellRef.current);
      else close(false, false);
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [open]);

  type Picker = HTMLElement & { hideFlyout?: () => void };
  const openSuggestions = (): Picker[] =>
    Array.from(panelRef.current?.querySelectorAll<Picker>('mgt-people-picker') ?? [])
      .filter(picker => pickerListOpen(picker));

  // Caught on the way down, before the picker itself sees the key. The event arrives retargeted to the
  // picker element.
  const onPanelKeyDownCapture = (event: React.KeyboardEvent) => {
    const pickers = Array.from(panelRef.current?.querySelectorAll<Picker>('mgt-people-picker') ?? []);
    const at = pickers.indexOf(event.target as Picker);

    // Escape closes a picker's suggestion list first; only an Escape with none showing closes the panel.
    if (event.key === 'Escape') {
      const showing = openSuggestions();
      if (!showing.length) return;
      event.preventDefault();
      event.stopPropagation();
      showing.forEach(picker => picker.hideFlyout?.());
      return;
    }

    // As in Add Ticket: with a list showing, Tab does what Enter does - the picker takes the lit person (it does
    // that on both keys; after a pick the list shows again with its first person lit, so Escape it to move on)
    // and focus stays in the box. With none showing, Tab moves to the next box, or off either end to the
    // buttons, and Enter saves, or closes when nothing changed.
    if (at < 0) return;
    if (event.key === 'Tab' && openSuggestions().length) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      openSuggestions().forEach(picker => picker.hideFlyout?.());
      const backward = event.shiftKey;
      // Only a box that can take typing is a stop: the Requester box holding its one person has no input,
      // and stopping on the panel there let the next Shift+Tab leave the panel for the pager.
      const typable = typablePickers();
      const next = typable[typable.indexOf(event.target as Picker) + (backward ? -1 : 1)];
      setTimeout(() => {
        if (next) next.focus();
        else focusActions(panelRef.current, backward);
      }, 0);
    } else if (event.key === 'Enter' && !openSuggestions().length) {
      event.preventDefault();
      event.stopPropagation();
      openSuggestions().forEach(picker => picker.hideFlyout?.());
      if (canSaveRef.current) close(true, true);
      else if (!dirtyRef.current) close(false, true);
    }
  };

  const typablePickers = () =>
    Array.from(panelRef.current?.querySelectorAll<Picker & { input?: HTMLElement }>('mgt-people-picker') ?? []).filter(picker => picker.input);

  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      close(false, true);
      return;
    }
    if (isPanelAction(event.target)) {
      wrapActionTab(event, panelRef.current, typablePickers());
      return;
    }
    // The panel itself has focus when no box can take typing (both hold their people): Tab goes to a box or
    // the buttons, Enter does what it does in a box.
    if (event.target !== panelRef.current) return;
    if (event.key === 'Tab') {
      event.preventDefault();
      const typable = typablePickers();
      if (!event.shiftKey && typable.length) typable[0].focus();
      else focusActions(panelRef.current, event.shiftKey);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (canSaveRef.current) close(true, true);
      else if (!dirtyRef.current) close(false, true);
    }
  };

  // Focus moving to another control outside the panel closes it, as a click away does. Focus dropping to the page body is not:
  // that is what happens when a picker redraws and replaces the box that had it. Clicks outside are handled
  // by the mousedown listener, and Tab off either end by onPanelKeyDownCapture.
  const onPanelBlur = () => {
    setTimeout(() => {
      const active = document.activeElement;
      if (!panelRef.current || !active || active === document.body) return;
      if (!panelRef.current.contains(active) && active !== cellRef.current && !dirtyRef.current) close(false, false);
    }, 0);
  };

  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <ProviderConsumer
      render={globalTheme => {
        const marked = markClass(saveMark.mark, saveMark.edited);
        const lock = savingCell(saveMark.busy);
        return (
        <>
          <div
            ref={cellRef}
            className={`inline-edit-cell${marked}${lock.className}`}
            {...lock.props}
            data-selection-disabled={true}
            tabIndex={0}
            role="button"
            aria-label={`${i18next.t('ticket.ticket-list.requester')}: ${requester?.FullName ?? ''}`}
            aria-expanded={open}
            onMouseDown={stop}
            onClick={event => {
              stop(event);
              if (open) return;
              saveMark.opening();
              pickedRequesterRef.current = null;
              pickedAffectedRef.current = null;
              setOpen(true);
            }}
            onKeyDown={event => {
              if (event.key === 'Escape' && open) { stop(event); event.preventDefault(); close(false, true); return; }
              if (!open && (event.key === 'Enter' || event.key === 'F2')) {
                stop(event);
                event.preventDefault();
                pickedRequesterRef.current = null;
                pickedAffectedRef.current = null;
                setOpen(true);
              }
            }}
            style={{
              ...CELL_BOX,
              
              cursor: 'text',
              outline: open ? `1px solid ${globalTheme.siteVariables.colorScheme.brand.foreground}` : undefined,
              outlineOffset: -1, ...saveMark.markStyle
            }}
          >
            {resting}
          </div>
          {open && position && ReactDOM.createPortal(
            <div
              ref={panelRef}
              {...PANEL_PORTAL}
              role="dialog"
              tabIndex={-1}
              aria-label={i18next.t('ticket.ticket-list.requester')}
              className="rounded"
              onMouseDown={stop}
              onClick={stop}
              onDoubleClick={stop}
              onKeyDownCapture={onPanelKeyDownCapture}
              onKeyDown={onPanelKeyDown}
              onBlur={onPanelBlur}
              style={{
                position: 'fixed',
                left: position.left,
                ...(position.bottom === undefined ? { top: position.top } : { bottom: position.bottom }),
                width: PANEL_WIDTH,
                zIndex: 1000000,
                outline: 'none',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                backgroundColor: globalTheme.siteVariables.colorScheme.default.background,
                // The panel is portalled to the page's body, outside the themed app, so its text takes the theme's colour here.
                color: globalTheme.siteVariables.colorScheme.default.foreground,
                boxShadow: getDropdownBoxShadow(globalTheme),
                ...ThemeColorScheme(globalTheme.siteVariables)
              }}
            >
              <div>
                <Text style={{ fontSize: 14 }}>{i18next.t('ticket.add-ticket.requester')}<RequiredAsterisk /></Text>
                <div className="rounded" aria-invalid={requesterEmpty}>
                <PeoplePicker
                  className="inline-requester-picker"
                  userType={UserType.user}
                  type={PersonType.person}
                  selectionMode="single"
                  selectionChanged={(event: Event) => {
                    // As on the ticket page, a requester cannot be cleared - an empty pick is ignored.
                    const picked = pickedFrom(event);
                    setRequesterEmpty(picked.length === 0);
                    if (picked.length > 0) pickedRequesterRef.current = picked[0];
                    setPicksChanged(n => n + 1);
                  }}
                />
                </div>
                {requesterEmpty && <FieldErrorText>{i18next.t('ticket-details.edit-ticket-view.required-field-must-be-filled-out')}</FieldErrorText>}
              </div>
              {affectedUsersEnabled && (
                <div>
                  <Text content={i18next.t('ticket.ticket-list.affected-users')} style={{ fontSize: 14 }} />
                  <PeoplePicker
                    className="inline-affected-picker"
                    userType={UserType.user}
                    type={PersonType.person}
                    selectionChanged={(event: Event) => { pickedAffectedRef.current = [...pickedFrom(event)]; setPicksChanged(n => n + 1); }}
                  />
                </div>
              )}
              <PanelActions canSave={canSave} onSave={() => close(true, true)} onCancel={() => close(false, true)} />
            </div>,
            document.body
          )}
        </>
        );
      }}
    />
  );
};
